import React, { useEffect, useRef, useState } from 'react';
import { personalInfo } from '../data/portfolio';

// Individual word that reveals from bottom
const RevealWord = ({ word, delay, isVisible }) => (
  <span className="word inline-block overflow-hidden">
    <span
      className="word-inner inline-block"
      style={{
        transform: isVisible ? 'translateY(0)' : 'translateY(110%)',
        transition: `transform 0.75s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      }}
    >
      {word}&nbsp;
    </span>
  </span>
);

// A single display line with word-reveal animation
const HeroLine = ({ text, isVisible, baseDelay = 0, className = '' }) => {
  const words = text.split(' ');
  return (
    <div className={`overflow-hidden ${className}`} aria-label={text}>
      {words.map((word, i) => (
        <RevealWord
          key={i}
          word={word}
          isVisible={isVisible}
          delay={baseDelay + i * 60}
        />
      ))}
    </div>
  );
};

const Hero = () => {
  const [visible, setVisible] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    // Trigger after a short mount delay for dramatic entrance
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  const scrollDown = () => {
    const el = document.getElementById('about');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ backgroundColor: 'var(--color-bg)' }}
    >
      {/* Subtle grid background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(38,38,38,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(38,38,38,0.4) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      {/* Very subtle accent gradient blob — top right */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(183,255,60,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 pt-24 pb-16">
        {/* Eyebrow label */}
        <div
          className="section-label mb-10 md:mb-14 animate-fade-in"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.6s ease 0.1s',
          }}
        >
          FULL STACK × AI × ENGINEERING
        </div>

        {/* Main headline */}
        <h1 className="leading-none tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
          {/* Line 1 */}
          <HeroLine
            text="I BUILD"
            isVisible={visible}
            baseDelay={300}
            className="text-[clamp(4rem,13vw,12rem)] font-bold mb-1"
          />

          {/* Line 2 — DIGITAL in accent */}
          <div className="overflow-hidden">
            <div
              style={{
                transform: visible ? 'translateY(0)' : 'translateY(110%)',
                transition: 'transform 0.8s cubic-bezier(0.22,1,0.36,1) 560ms',
                fontSize: 'clamp(4rem, 13vw, 12rem)',
                fontWeight: 700,
                color: 'var(--color-accent)',
                lineHeight: 1,
                display: 'block',
              }}
            >
              DIGITAL
            </div>
          </div>

          {/* Line 3 */}
          <HeroLine
            text="PRODUCTS."
            isVisible={visible}
            baseDelay={700}
            className="text-[clamp(4rem,13vw,12rem)] font-bold"
          />
        </h1>

        {/* Supporting statement + stats */}
        <div
          className="mt-12 md:mt-16 max-w-xl"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.7s ease 1.1s, transform 0.7s ease 1.1s',
          }}
        >
          <p
            className="text-base md:text-lg leading-relaxed mb-8"
            style={{ color: 'var(--color-text2)' }}
          >
            {personalInfo.tagline}
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap gap-6 md:gap-10">
            {personalInfo.stats.map((stat, i) => (
              <div key={i}>
                <div
                  className="text-2xl md:text-3xl font-bold"
                  style={{ color: 'var(--color-text)' }}
                >
                  {stat.value}
                </div>
                <div
                  className="font-mono text-xs mt-1 uppercase tracking-wider"
                  style={{ color: 'var(--color-text2)' }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <button
          onClick={scrollDown}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 group"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.6s ease 1.6s',
          }}
          aria-label="Scroll to explore"
        >
          <span
            className="font-mono text-xs tracking-widest"
            style={{ color: 'var(--color-text2)' }}
          >
            SCROLL TO EXPLORE
          </span>
          <span
            className="animate-scroll-bounce text-lg"
            style={{ color: 'var(--color-text2)' }}
          >
            ↓
          </span>
        </button>
      </div>
    </section>
  );
};

export default Hero;
